import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { useFormikContext, getIn } from 'formik';
import { Form, Nav, Spinner, Tab, Alert, DropdownButton, Dropdown, Row, Col, Button } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';
import i18next from 'i18next';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Typeahead } from 'react-bootstrap-typeahead';
import SecondaryButton from '../../../components/SecondaryButton';
import { useAuthFetchFromRestAPI } from '../../../util/api';
import ExercisePickerModal, { CustomExercise } from '../modals/exercise-picker/ExercisePickerModal';
import LoadTemplateModal from '../modals/LoadTemplateModal';
import SaveTemplateModel from '../modals/SaveTemplateModel';
import { TrainingDto, EnduranceTrainingDto } from '../../../../../shared/dtos/trainingplan.dto';
import DeleteTrainingModal from '../modals/DeleteTrainingModal';
import { TrainingPlanTemplateRequest } from '../../../../../shared/requests/trainingPlanTemplate.request';
import { ExerciseCard } from './ExcerciseCard';
import { TrainingType } from '../../../../../shared/utils/enums';
import { ExerciseDto } from '../../../../../shared/dtos/exercise.dto';
import { EnduranceTrainingTypeDto } from '../../../../../shared/dtos/enduranceTrainingType.dto';
import DraggableTab from '../../../components/DraggableTab';
import LexicalEditor from '../../../components/textEditor/LexicalEditor';
import { useMaxTrainingCount } from '../../../hooks/useTenantSettings';
import { parseNumberInput } from '../../../util/number-utils';
import {
  ExerciseFormValue, isPauseSkippedByDefault,
  splitTrainingRequests, toDefaultExerciseUnit, toEnduranceTrainingFormValues, toTrainingFormValues,
  TrainingFormValues,
  TrainingPlanFormValues
} from "../trainingPlan.types";

export type InvalidTrainingFocus = {
  index: number;
  attempt: number;
};

type TrainingPlanFormProps = {
  invalidTrainingFocus?: InvalidTrainingFocus;
};

const TrainingPlanForm: FC<TrainingPlanFormProps> = ({ invalidTrainingFocus }: TrainingPlanFormProps) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<TrainingPlanFormValues>();
  const { authFetch } = useAuthFetchFromRestAPI();
  const maxTrainingCount: number = useMaxTrainingCount();

  const [errorMessage] = useState<string | null>(null);
  const [isSaving] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [showAddExerciseModal, setShowAddExerciseModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [enduranceTypes, setEnduranceTypes] = useState<EnduranceTrainingTypeDto[]>([]);

  useEffect(() => {
    if (invalidTrainingFocus) {
      setTabIndex(invalidTrainingFocus.index);
    }
  }, [invalidTrainingFocus]);

  const hasTrainingError = (index: number): boolean =>
    Boolean(getIn(errors, `trainings.${index}`)) && Boolean(getIn(touched, `trainings.${index}`));

  const userLanguage = i18next.language;

  const fetchEnduranceTypes = useCallback(async (): Promise<void> => {
    try {
      const result: EnduranceTrainingTypeDto[] = await authFetch('api/v1/endurance-training-type', { method: 'GET' });
      setEnduranceTypes(result);
    } catch (error) {
      console.error(error);
    }
  }, [authFetch]);

  useEffect(() => {
    void fetchEnduranceTypes();
  }, [fetchEnduranceTypes]);

  const saveTrainingPlanTemplate = async (isGlobalTemplate: boolean, templateName: string): Promise<void> => {
    const { trainings, enduranceTrainings } = splitTrainingRequests(values.trainings);

    const request: TrainingPlanTemplateRequest = {
      isGlobalTemplate,
      templateName,
      trainings,
      enduranceTrainings,
    };

    await authFetch('/api/v1/template', { method: 'POST', body: request });

    setShowSaveModal(false);
  };

  const addTraining = async (kind: TrainingType): Promise<void> => {
    if (values.trainings.length >= maxTrainingCount) {
      return;
    }

    const training: TrainingFormValues = {
      name: `${i18next.t('trainingPlan.newTabTraining')} ${values.trainings.length + 1}`,
      workoutsPerWeek: 3,
      workoutPause: 60,
      exercises: [],
      trainingKind: kind,
      enduranceTrainingType: undefined,
    };

    await setFieldValue('trainings', [...values.trainings, training]);
    setTabIndex(values.trainings.length);
  };

  const deleteTraining = async (index: number): Promise<void> => {
    const updatedTrainings = values.trainings.filter((_, trainingIndex) => trainingIndex !== index);

    await setFieldValue('trainings', updatedTrainings);

    setTabIndex(Math.max(updatedTrainings.length - 1, 0));
    setShowDeleteModal(false);
  };

  const moveTab = (dragIndex: number, hoverIndex: number): void => {
    const trainings = [...values.trainings];
    const [draggedTab] = trainings.splice(dragIndex, 1);
    trainings.splice(hoverIndex, 0, draggedTab);
    void setFieldValue('trainings', trainings);

    if (tabIndex === dragIndex) {
      setTabIndex(hoverIndex);
    } else if (tabIndex > dragIndex && tabIndex <= hoverIndex) {
      setTabIndex(tabIndex - 1);
    } else if (tabIndex < dragIndex && tabIndex >= hoverIndex) {
      setTabIndex(tabIndex + 1);
    }
  };

  const addTemplateToTraining = async (
    selectedTrainings: TrainingDto[],
    selectedEnduranceTrainings: EnduranceTrainingDto[]
  ): Promise<void> => {
    if (values.trainings.length >= maxTrainingCount) {
      setShowTemplateModal(false);
      return;
    }

    const trainings: TrainingFormValues[] = [
      ...selectedTrainings.map((training) => ({ order: training.order, form: toTrainingFormValues(training) })),
      ...selectedEnduranceTrainings.map((training) => ({ order: training.order, form: toEnduranceTrainingFormValues(training) })),
    ]
      .sort((a, b) => a.order - b.order)
      .map((item) => item.form);

    const updatedTrainings = [...values.trainings, ...trainings];
    await setFieldValue('trainings', updatedTrainings);

    setTabIndex(updatedTrainings.length - 1);
    setShowTemplateModal(false);
  };

  const addExercises = async (exercises: ExerciseDto[]): Promise<void> => {
    const exerciseForms: ExerciseFormValue[] = exercises.map((exercise: ExerciseDto) => ({
      name: userLanguage === 'en' ? exercise.enName : exercise.deName,
      description: userLanguage === 'en' ? exercise.enDescription : exercise.deDescription,
      sets: 1,
      skipPause: isPauseSkippedByDefault(exercise.exerciseType),
      needsVideoFeedback: false,
      useCustomDescription: false,
      time: undefined,
      repetition: undefined,
      unit: toDefaultExerciseUnit(exercise.exerciseType),
      exerciseType: exercise.exerciseType,
      exercise: exercise,
    }));

    await setFieldValue(
      `trainings.${tabIndex}.exercises`,
      [...values.trainings[tabIndex].exercises, ...exerciseForms]
    );
    void setFieldTouched(`trainings.${tabIndex}.exercises`, true);

    setShowAddExerciseModal(false);

    window.scrollTo({
      top: document.body.clientHeight + 200,
      left: 0,
      behavior: 'smooth',
    });
  };

  const addCustomExercise = async (exercise: CustomExercise): Promise<void> => {
    const exerciseForm: ExerciseFormValue = {
      name: userLanguage === 'en' ? exercise.enName : exercise.deName,
      description: userLanguage === 'en' ? exercise.enDescription : exercise.deDescription,
      sets: 1,
      skipPause: isPauseSkippedByDefault(exercise.exerciseType),
      needsVideoFeedback: false,
      useCustomDescription: true,
      time: undefined,
      repetition: undefined,
      unit: toDefaultExerciseUnit(exercise.exerciseType),
      exerciseType: exercise.exerciseType,
    };

    await setFieldValue(`trainings.${tabIndex}.exercises`, [...values.trainings[tabIndex].exercises, exerciseForm]);
    void setFieldTouched(`trainings.${tabIndex}.exercises`, true);

    setShowAddExerciseModal(false);

    window.scrollTo({ top: document.body.clientHeight + 200, left: 0, behavior: 'smooth' });
  };

  return (
    <div className="position-relative">
      {isSaving && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: 'rgba(255,255,255,0.6)', zIndex: 2 }}
        >
          <Spinner animation="border" role="status"/>
        </div>
      )}

      {errorMessage && (
        <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
      )}

      <Tab.Container
        id="training-tabs"
        defaultActiveKey={tabIndex}
        activeKey={tabIndex}
        onSelect={(index) => setTabIndex(Number(index))}
      >
        <div className="bg-white p-4 pb-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-3">
              <i className="fas fa-dumbbell fs-5" style={{ color: '#234a68' }} aria-hidden="true" />
              <h1 className="h4 mb-0 fw-semibold">{i18next.t('trainingPlanForm.title')}</h1>
            </div>

            <div className="d-flex gap-3">
              <SecondaryButton
                onClick={() => setShowSaveModal(true)}
                disabled={values.trainings.length > maxTrainingCount}
              >
                <i className="fas fa-clone"/>{' '}
                <span className="hidden-xs">{i18next.t('trainingPlanForm.saveAsTemplate')}</span>
              </SecondaryButton>

              <SecondaryButton
                onClick={() => setShowTemplateModal(true)}
                disabled={values.trainings.length >= maxTrainingCount}
              >
                <i className="fas fa-folder"/>{' '}
                <span className="hidden-xs">{i18next.t('trainingPlanForm.loadTemplate')}</span>
              </SecondaryButton>

              <DropdownButton
                variant="primary"
                disabled={values.trainings.length >= maxTrainingCount}
                title={
                  <>
                    <i className="fas fa-plus"/>{' '}
                    <span className="hidden-xs">{i18next.t('trainingPlanForm.addTraining')}</span>
                  </>
                }
              >
                <Dropdown.Item onClick={() => addTraining(TrainingType.STRENGTH)}>
                  {i18next.t('trainingPlanForm.addNormalTraining')}
                </Dropdown.Item>
                <Dropdown.Item onClick={() => addTraining(TrainingType.ENDURANCE)}>
                  {i18next.t('trainingPlanForm.addEnduranceTraining')}
                </Dropdown.Item>
              </DropdownButton>
            </div>
          </div>

          <DndProvider backend={HTML5Backend}>
            <div className="overflow-auto mt-2">
              <Nav variant="tabs" className="clear flex-nowrap">
                {values.trainings.map((trainingItem, index) => (
                  <DraggableTab
                    key={`nav-${index}`}
                    index={index}
                    isActive={tabIndex === index}
                    training={trainingItem}
                    hasError={hasTrainingError(index)}
                    moveTab={moveTab}
                    onClick={() => setTabIndex(index)}
                  />
                ))}
              </Nav>
            </div>
          </DndProvider>
        </div>

        <div className="p-4 rounded-bottom" style={{ backgroundColor: '#f6f6f8' }}>
          <Tab.Content key={`tabContent-${values.trainings.length}`}>
            {values.trainings.map((trainingItem, index) => {
              const nameTouched = getIn(touched, `trainings.${index}.name`);
              const nameError = getIn(errors, `trainings.${index}.name`);

              const workoutsPerWeekTouched = getIn(touched, `trainings.${index}.workoutsPerWeek`);
              const workoutsPerWeekError = getIn(errors, `trainings.${index}.workoutsPerWeek`);

              const workoutPauseTouched = getIn(touched, `trainings.${index}.workoutPause`);
              const workoutPauseError = getIn(errors, `trainings.${index}.workoutPause`);

              const exercisesTouched = getIn(touched, `trainings.${index}.exercises`);
              const exercisesError = typeof getIn(errors, `trainings.${index}.exercises`) === 'string' ? getIn(errors, `trainings.${index}.exercises`) : undefined;

              const enduranceTypeTouched = getIn(touched, `trainings.${index}.enduranceTrainingType`);
              const enduranceTypeError = getIn(errors, `trainings.${index}.enduranceTrainingType`);

              const isEndurance = trainingItem.trainingKind === TrainingType.ENDURANCE;

              const nameField = (
                <div>
                  <Form.Label>{i18next.t('trainingPlanForm.trainingName')}</Form.Label>
                  <Form.Control
                    required
                    name={`trainings.${index}.name`}
                    type="text"
                    id={`trainingName-${index}`}
                    placeholder={i18next.t('trainingPlanForm.trainingName')}
                    value={trainingItem.name}
                    onChange={(event) => {
                      void setFieldValue(`trainings.${index}.name`, event.target.value);
                    }}
                    isInvalid={!!nameTouched && !!nameError}
                  />
                  <Form.Control.Feedback type="invalid">
                    {nameTouched && nameError}
                  </Form.Control.Feedback>
                </div>
              );

              const workoutsPerWeekField = (
                <div>
                  <Form.Label>{i18next.t('trainingPlanForm.workoutsPerWeek')}</Form.Label>
                  <Form.Control
                    required
                    type="number"
                    name={`trainings.${index}.workoutsPerWeek`}
                    id={`workoutsPerWeek-${index}`}
                    min="1"
                    max="7"
                    value={trainingItem.workoutsPerWeek ?? ''}
                    onChange={(event) => {
                      void setFieldValue(`trainings.${index}.workoutsPerWeek`, parseNumberInput(event.target.value));
                    }}
                    isInvalid={!!workoutsPerWeekTouched && !!workoutsPerWeekError}
                  />
                  <Form.Control.Feedback type="invalid">
                    {workoutsPerWeekTouched && workoutsPerWeekError}
                  </Form.Control.Feedback>
                </div>
              );

              const deleteButton = values.trainings.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="d-inline-flex align-items-center gap-2 flex-shrink-0 py-1 px-2"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <i className="fas fa-trash" aria-hidden="true" />
                  <span className="d-none d-sm-inline">{i18next.t('trainingPlanForm.deleteTraining')}</span>
                </Button>
              );

              return (
                <Tab.Pane key={`pane-${index}`} eventKey={index}>
                  <Form noValidate>
                    <div className="border rounded-3 p-3">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h2 className="h5 mb-0 fw-semibold">{i18next.t('trainingPlanForm.trainingDetails')}</h2>
                        {deleteButton}
                      </div>

                      <Row className="g-3">
                        <Col md={4}>{nameField}</Col>
                        <Col md={4}>{workoutsPerWeekField}</Col>
                        <Col md={4}>
                          {isEndurance ? (
                            <div>
                              <Form.Label>{i18next.t('trainingPlanForm.enduranceType')}</Form.Label>
                              <Typeahead
                                id={`endurance-type-${index}`}
                                labelKey={(option: any) => (i18next.language === 'de' ? option.deName : option.enName)}
                                options={enduranceTypes}
                                selected={trainingItem.enduranceTrainingType ? [trainingItem.enduranceTrainingType] : []}
                                onChange={(selected: any[]) =>
                                  setFieldValue(
                                    `trainings.${index}.enduranceTrainingType`,
                                    selected.length > 0 ? selected[0] : undefined
                                  )
                                }
                                onBlur={() => setFieldTouched(`trainings.${index}.enduranceTrainingType`, true)}
                                clearButton
                                positionFixed
                                flip
                                maxHeight="16rem"
                                disabled={enduranceTypes.length === 0}
                              />
                              {enduranceTypeTouched && enduranceTypeError && (
                                <div className="invalid-feedback d-block">
                                  {enduranceTypeError}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>
                              <Form.Label>{i18next.t('trainingPlanForm.workoutPause')}</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  required
                                  type="number"
                                  name={`trainings.${index}.workoutPause`}
                                  id={`workoutPause-${index}`}
                                  min="10"
                                  max="180"
                                  value={trainingItem.workoutPause ?? ''}
                                  onChange={(event) => {
                                    void setFieldValue(`trainings.${index}.workoutPause`, parseNumberInput(event.target.value));
                                  }}
                                  isInvalid={!!workoutPauseTouched && !!workoutPauseError}
                                />
                                <InputGroup.Text id={`workoutPause-suffix-${index}`}>
                                  {i18next.t('trainingPlanForm.pauseGroup')}
                                </InputGroup.Text>
                                <Form.Control.Feedback type="invalid">
                                  {workoutPauseTouched && workoutPauseError}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </div>
                          )}
                        </Col>
                      </Row>

                      {isEndurance && (
                        <div className="mt-3">
                          <Form.Label>{i18next.t('trainingPlanForm.enduranceDescription')}</Form.Label>
                          <LexicalEditor
                            key={`trainings.${index}.description`}
                            editable
                            value={trainingItem.description ?? ''}
                            placeholder={i18next.t('trainingPlanForm.enduranceDescription')}
                            onChange={(value: string) => setFieldValue(`trainings.${index}.description`, value)}
                            onBlur={() => setFieldTouched(`trainings.${index}.description`, true)}
                          />
                        </div>
                      )}
                    </div>
                  </Form>

                  {!isEndurance && (
                    <div className="pt-5 w-100">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <i className="fas fa-list fs-5" style={{ color: '#234a68' }} aria-hidden="true" />
                          <h2 className="h5 mb-0 fw-semibold">{i18next.t('trainingPlanForm.exercise')}</h2>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => setShowAddExerciseModal(true)}
                          aria-label={i18next.t('trainingPlanForm.addExercise')}
                        >
                          <i className="fas fa-plus" aria-hidden="true" />
                        </Button>
                      </div>

                      <div key={`exerciseList-${trainingItem.exercises.length}`}>
                        {trainingItem.exercises.length ? (
                          <DndProvider backend={HTML5Backend}>
                            <ExerciseCard tabIndex={index}/>
                          </DndProvider>
                        ) : (
                          <p className={`text-center mb-0 ${exercisesTouched && exercisesError ? 'text-danger' : ''}`}>
                            {exercisesTouched && exercisesError ? exercisesError : i18next.t('trainingPlanForm.noExercise')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </div>

        <DeleteTrainingModal
          show={showDeleteModal}
          onPositive={(index: number) => deleteTraining(index)}
          onNegative={() => setShowDeleteModal(false)}
          trainingIndex={tabIndex}
        />

        <LoadTemplateModal
          show={showTemplateModal}
          onPositive={(selectedTrainings: TrainingDto[], selectedEnduranceTrainings: EnduranceTrainingDto[]) =>
            addTemplateToTraining(selectedTrainings, selectedEnduranceTrainings)
          }
          onNegative={() => setShowTemplateModal(false)}
          trainingIndex={values.trainings.length}
          maxTrainingCount={maxTrainingCount}
        />

        <SaveTemplateModel
          show={showSaveModal}
          onPositive={saveTrainingPlanTemplate}
          onNegative={() => setShowSaveModal(false)}
        />
      </Tab.Container>

      <ExercisePickerModal
        show={showAddExerciseModal}
        onCreateExercise={(exercises) => void addExercises(exercises)}
        onCreateCustomExercise={(exercise) => void addCustomExercise(exercise)}
        onNegative={() => setShowAddExerciseModal(false)}
      />
    </div>
  );
};

export default memo(TrainingPlanForm);
